import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { QRCodeSVG } from 'qrcode.react';
import PhoneInput from './PhoneInput';
import EditEventModal from './EditEventModal';
import EventAttendeesModal from './EventAttendeesModal';
import { validateName, validatePhone, extractPhoneDigits } from '../utils/validation';
import { formatDate } from '../utils/formatting';

// Helper function to get localized text from bilingual object
function getLocalizedText(textObj, lang = 'en') {
    if (typeof textObj === 'string') return textObj; // Legacy support
    if (typeof textObj === 'object' && textObj !== null) {
        return textObj[lang] || textObj.en || '';
    }
    return '';
}

function EventDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language?.startsWith('tl') ? 'tl' : 'en';
    const { currentUser, isAdmin } = useAuth();

    const [event, setEvent] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [signupStatus, setSignupStatus] = useState({});

    // Anonymous signup modal state
    const [showSignupModal, setShowSignupModal] = useState(false);
    const [anonymousForm, setAnonymousForm] = useState({ name: '', phone: '+63' });
    const [anonymousFormErrors, setAnonymousFormErrors] = useState({ name: '', phone: '' });
    const [anonymousFormLoading, setAnonymousFormLoading] = useState(false);

    // Edit event modal state
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    // Attendees modal state
    const [isAttendeesModalOpen, setIsAttendeesModalOpen] = useState(false);

    // QR Code state
    const [showQRCode, setShowQRCode] = useState(false);
    const qrRef = useRef(null);
    const fetchEvent = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await fetch(`/api/events/${id}`);
            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error('Event not found');
                }
                throw new Error('Failed to fetch event');
            }
            const data = await response.json();
            setEvent(data);
            setError(null);
        } catch (error) {
            setError(error);
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchEvent();
    }, [fetchEvent]);

    // Helper to get slots info
    function getSlotsInfo() {
        if (!event) return { maxSlots: 5, attendeesCount: 0, slotsRemaining: 5, isFull: false };
        const maxSlots = event.maxSlots || 5;
        const attendeesCount = event.attendees?.length || 0;
        const slotsRemaining = maxSlots - attendeesCount;
        const isFull = slotsRemaining <= 0;
        return { maxSlots, attendeesCount, slotsRemaining, isFull };
    }

    // Handle signup button click
    function handleSignupClick() {
        const { isFull } = getSlotsInfo();

        if (isFull) {
            setSignupStatus({ error: 'This event is full' });
            return;
        }

        if (currentUser) {
            handleAuthenticatedSignup();
        } else {
            setAnonymousForm({ name: '', phone: '+63' });
            setAnonymousFormErrors({ name: '', phone: '' });
            setShowSignupModal(true);
        }
    }

    // Handle authenticated user signup
    async function handleAuthenticatedSignup() {
        try {
            setSignupStatus({ loading: true });

            const token = await currentUser.getIdToken();
            const response = await fetch(`/api/events/${id}/signup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    userId: currentUser.uid,
                    email: currentUser.email,
                    isAnonymous: false
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to sign up');
            }

            setSignupStatus({ success: true, message: 'Successfully signed up!' });
            fetchEvent();
        } catch (error) {
            setSignupStatus({ error: error.message });
        }
    }

    // Handle anonymous signup form submission
    async function handleAnonymousSignup(e) {
        e.preventDefault();

        const nameValidation = validateName(anonymousForm.name);
        const phoneValidation = validatePhone(anonymousForm.phone);

        const errors = {
            name: nameValidation.error || '',
            phone: phoneValidation.error || ''
        };

        setAnonymousFormErrors(errors);

        if (!nameValidation.isValid || !phoneValidation.isValid) {
            return;
        }

        try {
            setAnonymousFormLoading(true);
            setAnonymousFormErrors({ name: '', phone: '' });

            const phoneDigits = extractPhoneDigits(anonymousForm.phone);

            const response = await fetch(`/api/events/${id}/signup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: anonymousForm.name.trim(),
                    phone: phoneDigits,
                    phoneFormatted: anonymousForm.phone,
                    isAnonymous: true
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to sign up');
            }

            setSignupStatus({ success: true, message: 'Successfully signed up!' });
            setShowSignupModal(false);
            setAnonymousForm({ name: '', phone: '+63' });
            fetchEvent();
        } catch (error) {
            setAnonymousFormErrors(prev => ({ ...prev, general: error.message }));
        } finally {
            setAnonymousFormLoading(false);
        }
    }

    // Check if event is in the past
    const isPast = event ? new Date(event.start_time) < new Date() : false;
    const { maxSlots, attendeesCount, slotsRemaining, isFull } = getSlotsInfo();

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="loading-text">{t('loading')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page-section">
                <div className="event-detail-header">
                    <button
                        className="back-btn"
                        onClick={() => navigate('/events')}
                        aria-label={t('event_detail_back')}
                    >
                        <span className="back-arrow">←</span>
                        <span>{t('event_detail_back')}</span>
                    </button>
                </div>
                <div className="error-container">
                    <div className="error-icon">⚠️</div>
                    <p className="error-message">{t('event_detail_not_found')}</p>
                    <Link to="/events" className="btn-primary-modern">
                        {t('event_detail_view_all')}
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="page-section event-detail-page">
            {/* Back Button Header */}
            <div className="event-detail-header">
                <button
                    className="back-btn"
                    onClick={() => navigate('/events')}
                    aria-label={t('event_detail_back')}
                >
                    <span className="back-arrow">←</span>
                    <span>{t('event_detail_back')}</span>
                </button>

                {/* Admin Actions */}
                {isAdmin && (
                    <div className="event-detail-admin-actions">
                        <button
                            className="btn-outline-modern"
                            onClick={() => setIsAttendeesModalOpen(true)}
                        >
                            👥 {t('admin_attendees')}
                        </button>
                        <button
                            className="btn-outline-modern"
                            onClick={() => setIsEditModalOpen(true)}
                        >
                            ✏️ {t('edit')}
                        </button>
                    </div>
                )}
            </div>

            {/* Event Content */}
            <article className="event-detail-content">
                {/* Event Status Badge */}
                {isPast && (
                    <div className="event-status-badge past">
                        🕰️ {t('event_ended')}
                    </div>
                )}

                {/* Event Title */}
                <h1 className="event-detail-title">
                    {getLocalizedText(event.name, currentLang)}
                </h1>

                {/* Event Meta Information */}
                <div className="event-detail-meta">
                    <div className="event-detail-meta-item">
                        <span className="event-detail-meta-icon">📅</span>
                        <div>
                            <span className="event-detail-meta-label">{t('when')}</span>
                            <span className="event-detail-meta-value">
                                {event.start_time ? formatDate(event.start_time, currentLang) : 'TBD'}
                            </span>
                        </div>
                    </div>

                    <div className="event-detail-meta-item">
                        <span className="event-detail-meta-icon">📍</span>
                        <div>
                            <span className="event-detail-meta-label">{t('where')}</span>
                            <span className="event-detail-meta-value">
                                {event.place?.name || event.place || 'TBD'}
                            </span>
                        </div>
                    </div>

                    {event.place?.address && (
                        <div className="event-detail-meta-item">
                            <span className="event-detail-meta-icon">🗺️</span>
                            <div>
                                <span className="event-detail-meta-label">{t('address')}</span>
                                <span className="event-detail-meta-value">{event.place.address}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Event Description */}
                <div className="event-detail-description">
                    <h2 className="event-detail-section-title">{t('description')}</h2>
                    <p>{getLocalizedText(event.description, currentLang)}</p>
                </div>

                {/* Google Maps Embed Section */}
                {(event.place?.mapsUrl || (event.place?.lat && event.place?.lng)) && (
                    <div className="event-map-section">
                        <h2 className="event-detail-section-title">🗺️ {t('event_location_map')}</h2>
                        <div className="event-map-container">
                            <iframe
                                title={`${getLocalizedText(event.name, currentLang)} - ${t('event_location_map')}`}
                                src={(() => {
                                    let url = event.place?.mapsUrl;

                                    // 1. If it's a full iframe code paste, extract the src
                                    if (url && url.includes('<iframe')) {
                                        const match = url.match(/src="([^"]+)"/);
                                        if (match) return match[1];
                                    }

                                    // 2. If it's already an embed URL (standard or keyless), use it as is
                                    if (url && (url.includes('/embed') || url.includes('output=embed'))) {
                                        return url;
                                    }

                                    // 3. Construct a query for the Keyless Embed API
                                    // This format (output=embed) is free and doesn't require the Maps API to be enabled in Cloud Console.
                                    let query = '';
                                    if (event.place?.name) {
                                        query = event.place.name;
                                        if (event.place.address) query += `, ${event.place.address}`;
                                    } else if (event.place?.lat && event.place?.lng) {
                                        query = `${event.place.lat},${event.place.lng}`;
                                    } else if (url) {
                                        // Handle standard desktop links
                                        if (url.includes('/maps/place/') || url.includes('/maps/search/')) {
                                            const parts = url.split('/');
                                            const index = parts.indexOf('place') !== -1 ? parts.indexOf('place') : parts.indexOf('search');
                                            if (index !== -1 && parts[index + 1]) {
                                                query = parts[index + 1].split('?')[0];
                                            }
                                        }
                                    }

                                    // If we have a query, use the reliable keyless embed format
                                    if (query) {
                                        return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
                                    }

                                    // Last resort: return the raw URL or an empty string
                                    return url || '';
                                })()}
                                width="100%"
                                height="300"
                                style={{ border: 0, borderRadius: '12px' }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                        {(event.place?.lat && event.place?.lng) && (
                            <div className="event-map-actions">
                                <a
                                    href={`https://www.google.com/maps/dir/?api=1&destination=${event.place.lat},${event.place.lng}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-outline-modern"
                                >
                                    🧭 {t('event_get_directions')}
                                </a>
                                <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${event.place.lat},${event.place.lng}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-outline-modern"
                                >
                                    📍 {t('event_view_on_maps')}
                                </a>
                            </div>
                        )}
                    </div>
                )}

                {/* Slots Indicator */}
                <div className={`slots-indicator large ${isFull ? 'full' : slotsRemaining <= 2 ? 'low' : ''}`}>
                    <span className="slots-icon">🎟️</span>
                    <span className="slots-text">
                        {isFull
                            ? t('events_slots_full')
                            : `${slotsRemaining} / ${maxSlots} ${t('events_slots_available')}`
                        }
                    </span>
                    <div className="slots-bar">
                        <div
                            className="slots-bar-fill"
                            style={{ width: `${(attendeesCount / maxSlots) * 100}%` }}
                        ></div>
                    </div>
                </div>

                {/* Signup Section */}
                <div className="event-detail-signup">
                    {signupStatus.success ? (
                        <div className="success-badge large">✓ {t('events_signup_success')}</div>
                    ) : signupStatus.error ? (
                        <div className="error-badge">{signupStatus.error}</div>
                    ) : isPast ? (
                        <button
                            className="btn-accent-modern btn-primary-modern btn-large btn-disabled"
                            disabled
                        >
                            {t('event_ended')}
                        </button>
                    ) : (
                        <button
                            className={`btn-accent-modern btn-primary-modern btn-large ${isFull ? 'btn-disabled' : ''}`}
                            onClick={handleSignupClick}
                            disabled={signupStatus.loading || isFull}
                        >
                            {signupStatus.loading
                                ? t('events_signing_up')
                                : isFull
                                    ? t('events_slots_full')
                                    : t('events_signup_btn')
                            }
                        </button>
                    )}
                </div>

                {/* QR Code Section */}
                <div className="event-qr-section">
                    <button
                        className="btn-outline-modern qr-toggle-btn"
                        onClick={() => setShowQRCode(!showQRCode)}
                    >
                        📱 {showQRCode ? t('close') : t('event_qr_code')}
                    </button>

                    {showQRCode && (
                        <div className="qr-code-container">
                            <div className="qr-code-content" ref={qrRef}>
                                <QRCodeSVG
                                    value={`${window.location.origin}/events/${id}`}
                                    size={200}
                                    level="H"
                                    includeMargin={true}
                                    bgColor="#ffffff"
                                    fgColor="#000000"
                                />
                            </div>
                            <p className="qr-code-subtitle">{t('event_qr_code_subtitle')}</p>
                            <button
                                className="btn-primary-modern qr-download-btn"
                                onClick={() => {
                                    // Create a canvas from SVG and download
                                    const svg = qrRef.current?.querySelector('svg');
                                    if (!svg) return;

                                    const canvas = document.createElement('canvas');
                                    const ctx = canvas.getContext('2d');
                                    const data = new XMLSerializer().serializeToString(svg);
                                    const svgBlob = new Blob([data], { type: 'image/svg+xml;charset=utf-8' });
                                    const url = URL.createObjectURL(svgBlob);

                                    const img = new Image();
                                    img.onload = () => {
                                        canvas.width = img.width;
                                        canvas.height = img.height;
                                        ctx.drawImage(img, 0, 0);
                                        URL.revokeObjectURL(url);

                                        const pngUrl = canvas.toDataURL('image/png');
                                        const downloadLink = document.createElement('a');
                                        const eventName = getLocalizedText(event.name, currentLang).replace(/[^a-z0-9]/gi, '_').substring(0, 30);
                                        downloadLink.download = `event-qr-${eventName}.png`;
                                        downloadLink.href = pngUrl;
                                        downloadLink.click();
                                    };
                                    img.src = url;
                                }}
                            >
                                📥 {t('event_download_qr')}
                            </button>
                        </div>
                    )}
                </div>
            </article>

            {/* Anonymous Signup Modal */}
            {showSignupModal && (
                <div className="modal-overlay" onClick={() => setShowSignupModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="modal-close-btn"
                            onClick={() => setShowSignupModal(false)}
                            aria-label="Close modal"
                        >
                            ✕
                        </button>

                        <h3 className="modal-title">🎟️ {t('events_modal_title')}</h3>
                        <p className="modal-subtitle">{t('events_modal_subtitle')}</p>

                        {anonymousFormErrors.general && (
                            <div className="error-banner">{anonymousFormErrors.general}</div>
                        )}

                        <form onSubmit={handleAnonymousSignup} className="modal-form">
                            <div className={`form-group ${anonymousFormErrors.name ? 'has-error' : ''}`}>
                                <label htmlFor="signup-name">{t('events_your_name')} <span className="required-indicator">*</span></label>
                                <input
                                    type="text"
                                    id="signup-name"
                                    className={`form-input ${anonymousFormErrors.name ? 'error' : ''}`}
                                    value={anonymousForm.name}
                                    onChange={(e) => {
                                        setAnonymousForm(prev => ({ ...prev, name: e.target.value }));
                                        if (anonymousFormErrors.name) {
                                            setAnonymousFormErrors(prev => ({ ...prev, name: '' }));
                                        }
                                    }}
                                    placeholder={t('events_enter_full_name')}
                                    autoFocus
                                    maxLength={100}
                                />
                                {anonymousFormErrors.name && (
                                    <span className="field-error">{anonymousFormErrors.name}</span>
                                )}
                            </div>

                            <PhoneInput
                                id="signup-phone"
                                value={anonymousForm.phone}
                                onChange={(phone) => {
                                    setAnonymousForm(prev => ({ ...prev, phone }));
                                    if (anonymousFormErrors.phone) {
                                        setAnonymousFormErrors(prev => ({ ...prev, phone: '' }));
                                    }
                                }}
                                required
                                showWhatsApp={true}
                                label={t('events_phone_label')}
                            />
                            {anonymousFormErrors.phone && (
                                <span className="field-error" style={{ marginTop: '-0.5rem' }}>{anonymousFormErrors.phone}</span>
                            )}

                            <div className="modal-actions">
                                <button
                                    type="button"
                                    className="btn-outline-modern"
                                    onClick={() => setShowSignupModal(false)}
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary-modern"
                                    disabled={anonymousFormLoading}
                                >
                                    {anonymousFormLoading ? t('events_signing_up') : t('events_confirm_signup')}
                                </button>
                            </div>
                        </form>

                        <p className="modal-footer-text">
                            {t('auth_already_have_account')} <Link to="/login" onClick={() => setShowSignupModal(false)}>{t('auth_login_link')}</Link> {t('events_login_faster')}
                        </p>
                    </div>
                </div>
            )}

            {/* Edit Event Modal */}
            <EditEventModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                event={event}
                onSuccess={() => {
                    fetchEvent();
                }}
            />

            {/* Event Attendees Modal */}
            <EventAttendeesModal
                isOpen={isAttendeesModalOpen}
                onClose={() => setIsAttendeesModalOpen(false)}
                event={event}
                onSuccess={() => {
                    fetchEvent();
                }}
            />
        </div>
    );
}

export default EventDetail;
